import React from 'react';
import { Button, Space, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { CardContainer } from './CardContainer';
import { SectionHeader } from './SectionHeader';
import { ContractDataRow } from './ContractDataRow';
import { GridLayout } from './GridLayout';
import { colors, spacing } from '../design-system/tokens';
import { Typography } from '../design-system/typography';

// Document icon SVG (matching design system style)
const DocumentIcon: React.FC = () => (
  <svg 
    width="18" 
    height="24" 
    viewBox="0 0 18 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M11 0H2C0.9 0 0 0.9 0 2V22C0 23.1 0.9 24 2 24H16C17.1 24 18 23.1 18 22V7L11 0Z" 
      fill="currentColor"
    />
    <path 
      d="M11 0V7H18" 
      fill="none"
      stroke="white"
      strokeWidth="1"
    />
  </svg>
);

export interface BookingDetailsData {
  id?: string; // Booking ID for edit/remove operations
  bookingNumber: string; // e.g., "1"
  carrier: string;
  bookingDate: string;
  vesselVoy: string;
  bookingRef: string; // Booking reference number (underlined)
  portOfLoading: string;
  portOfDelivery: string;
  docsCutoff: string;
  cyCut: string;
  etd: string;
  eta: string;
  specialInstructions?: string;
  containerPickup: {
    facility: string;
    address: string[];
  };
  containerDropoff: {
    facility: string;
    address: string[];
  };
  sendDocsTo: string;
}

interface BookingDetailsCardProps {
  booking?: BookingDetailsData; // Optional - if not provided, shows "no booking" state
  className?: string;
  onEdit?: () => void; // Called when edit button is clicked
  onRemove?: () => void; // Called when remove button is clicked
  onAdd?: () => void; // Called when add booking button is clicked
  onFindVessel?: () => void; // Called when find vessel link is clicked
  shipmentId?: string; // For context in operations
}

export const BookingDetailsCard: React.FC<BookingDetailsCardProps> = ({
  booking,
  className = '',
  onEdit,
  onRemove,
  onAdd,
  onFindVessel,
  shipmentId
}) => {
  // If no booking, show "no booking assigned" state
  if (!booking) {
    return (
      <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
        <CardContainer variant="secondary">
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            padding: spacing.xl,
            gap: spacing.md
          }}>
            <Typography 
              variant="h3" 
              as="div"
              style={{ 
                color: colors.text.secondary,
                textAlign: 'center'
              }}
            >
              No Booking Assigned
            </Typography>
            <Space>
              {onAdd && (
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                  onClick={onAdd}
                >
                  Add Booking
                </Button>
              )}
              {onFindVessel && (
                <Button
                  icon={<SearchOutlined />}
                  onClick={onFindVessel}
                >
                  Find Vessel
                </Button>
              )}
            </Space>
          </div>
        </CardContainer>
      </div>
    );
  }
  const renderMultiLineValue = (lines: string[]) => {
    return lines.map((line, index) => (
      <Typography 
        key={index}
        variant="valueAddress" 
        as="div"
        style={{ 
          color: colors.text.primary,
          lineHeight: 'normal',
          margin: 0
        }}
      >
        {line}
      </Typography>
    ));
  };

  const renderBookingNumber = (value: string) => (
    <Typography 
      variant="valueAddress" 
      as="div"
      style={{ 
        color: '#496e74', // Primary/60 color from Figma
        textDecoration: 'underline',
        textDecorationSkipInk: 'none',
        textDecorationStyle: 'solid',
        textUnderlinePosition: 'from-font',
        lineHeight: 'normal'
      }}
    >
      {value}
    </Typography>
  );

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: spacing.xl }}>
      <CardContainer variant="secondary">
        {/* Header with action buttons */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: spacing.md 
        }}>
          <SectionHeader
            title={`Booking ${booking.bookingNumber}`}
            icon={<div style={{ width: '18px', height: '24px' }}><DocumentIcon /></div>}
            showBottomBorder={false}
          />
          <Space>
            {onEdit && (
              <Button 
                type="text" 
                icon={<EditOutlined />} 
                onClick={onEdit}
                title="Edit booking"
              />
            )}
            {onRemove && (
              <Popconfirm
                title="Remove Booking"
                description="Are you sure you want to remove this booking from the shipment?"
                onConfirm={onRemove}
                okText="Yes, Remove"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
              >
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  title="Remove booking"
                />
              </Popconfirm>
            )}
          </Space>
        </div>
        
        <div style={{ 
          borderBottom: `1px solid ${colors.border.default}`,
          marginBottom: spacing.md 
        }} />

        {/* Carrier & Booking Date */}
        <GridLayout columns="1fr 1fr" gap="2xl">
          <ContractDataRow label="Carrier" value={booking.carrier} />
          <ContractDataRow label="Booking Date" value={booking.bookingDate} />
        </GridLayout>

        {/* Vessel/Voy & Booking Number */}
        <GridLayout columns="1fr 1fr" gap="2xl">
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <Typography
              variant="ledgerLabel"
              as="div"
              style={{
                color: colors.text.primary,
                textTransform: 'uppercase'
              }}
            >
              Vessel / Voy
            </Typography>
            {booking.vesselVoy ? (
              <Typography
                variant="valueAddress"
                as="div"
                style={{
                  color: colors.text.primary,
                  lineHeight: 'normal'
                }}
              >
                {booking.vesselVoy}
              </Typography>
            ) : (
              <Button
                type="link"
                icon={<SearchOutlined />}
                onClick={onFindVessel}
                style={{ padding: 0, height: 'auto', textAlign: 'left' }}
              >
                Find Vessel
              </Button>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <Typography
              variant="ledgerLabel"
              as="div"
              style={{
                color: colors.text.primary,
                textTransform: 'uppercase'
              }}
            >
              Booking Number
            </Typography>
            {renderBookingNumber(booking.bookingRef)}
          </div>
        </GridLayout>

        {/* Ports */}
        <GridLayout columns="1fr 1fr" gap="2xl">
          <ContractDataRow label="Port Of Loading" value={booking.portOfLoading} />
          <ContractDataRow label="Port of Delivery" value={booking.portOfDelivery} />
        </GridLayout>

        {/* Cutoff Dates */}
        <GridLayout columns="1fr 1fr" gap="2xl">
          <ContractDataRow label="Docs Cutoff" value={booking.docsCutoff} />
          <ContractDataRow label="CY Cut" value={booking.cyCut} />
        </GridLayout>

        {/* ETD & ETA */}
        <GridLayout columns="1fr 1fr" gap="2xl">
          <ContractDataRow label="ETD" value={booking.etd} />
          <ContractDataRow label="ETA" value={booking.eta} />
        </GridLayout>

        {/* Special Instructions */}
        {booking.specialInstructions && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
            <Typography 
              variant="ledgerLabel" 
              as="div"
              style={{ 
                color: colors.text.primary,
                textTransform: 'uppercase'
              }}
            >
              Special Instructions
            </Typography>
            <Typography 
              variant="valueAddress" 
              as="div"
              style={{ 
                color: colors.text.primary,
                lineHeight: 'normal'
              }}
            >
              {booking.specialInstructions}
            </Typography>
          </div>
        )}

        {/* Container Pick Up */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <Typography 
            variant="ledgerLabel" 
            as="div"
            style={{ 
              color: colors.text.primary,
              textTransform: 'uppercase'
            }}
          >
            Container Pick Up
          </Typography>
          <div>
            {renderMultiLineValue([booking.containerPickup.facility, ...booking.containerPickup.address])}
          </div>
        </div>

        {/* Container Drop Off */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
          <Typography 
            variant="ledgerLabel" 
            as="div"
            style={{ 
              color: colors.text.primary,
              textTransform: 'uppercase'
            }}
          >
            Container Drop Off
          </Typography>
          <div>
            {renderMultiLineValue([booking.containerDropoff.facility, ...booking.containerDropoff.address])}
          </div>
        </div>

        {/* Send Docs To */}
        <ContractDataRow label="Send Docs To" value={booking.sendDocsTo} />
      </CardContainer>
    </div>
  );
};

export default BookingDetailsCard;